import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import StatusBadge from '../components/redesign/StatusBadge';
import PriorityChip from '../components/redesign/PriorityChip';
import HubspotBadge from '../components/redesign/HubspotBadge';
import { Button, Card } from '../components/redesign/ui';
import { STATUS_DOT, PRIORITY } from '../lib/statusConfig';
import {
  Plus, ArrowsClockwise, WarningCircle, Ticket, Clock,
  CheckCircle, TrendUp, Tray,
} from '@phosphor-icons/react';

// KPI ordering matches the /tickets/stats bucket vocabulary. 'in_progress' is
// bucketed as 'Under Process' per D4; 'pending_third_party' holds pending_finance.
const STATUS_ORDER = ['new', 'escalated', 'in_progress', 'pending_third_party', 'resolved', 'closed'];
const STATUS_LABELS = {
  new: 'New',
  escalated: 'Escalated',
  in_progress: 'Under Process',
  pending_third_party: 'Pending Finance',
  resolved: 'Resolved',
  closed: 'Closed',
};
const STATUS_ICONS = {
  new: Ticket,
  escalated: WarningCircle,
  in_progress: TrendUp,
  pending_third_party: Clock,
  resolved: CheckCircle,
  closed: CheckCircle,
};

// Priority row config — bar colors from PRIORITY tokens, text tone paired.
const PRIORITY_ROWS = [
  { key: 'critical', label: 'Critical' },
  { key: 'high',     label: 'High' },
  { key: 'medium',   label: 'Medium' },
  { key: 'low',      label: 'Low' },
];

const getStatusCount = (stats, status) =>
  parseInt(stats?.byStatus?.find((s) => s.status === status)?.count || 0, 10);

const getPriorityCount = (stats, priority) =>
  parseInt(stats?.byPriority?.find((p) => p.complaint_priority === priority)?.count || 0, 10);

// Small teal-ish "HS" pill next to ticket number for HubSpot-only rows.
// Kept inline (rather than adding another component) — the shared HubspotBadge
// is a full-height "HubSpot" pill used for the Status column fallback instead.
const HsInline = () => (
  <span className="inline-flex rounded-chip border border-paper-border2 bg-paper-muted px-1 py-0.5 text-[10px] font-semibold text-ink-faint">
    HS
  </span>
);

export default function DashboardView({
  user,
  stats,
  recent = [],
  hsLoading,
  loading,
  onRefresh,
  onOpenPortalTicket,
  onOpenHsPreview,
  greetingWord = 'day',
  tagline = '',
  criticalCount = 0,
}) {
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ArrowsClockwise size={32} className="animate-spin text-status-under-process" />
      </div>
    );
  }

  const firstName = (user?.name || '').split(' ')[0] || 'there';
  const priorityTotal = (stats?.byPriority || []).reduce((a, b) => a + parseInt(b.count, 10), 0) || 1;
  const totalOpen = ['new', 'escalated', 'in_progress', 'pending_third_party']
    .reduce((acc, s) => acc + getStatusCount(stats, s), 0);

  return (
    <div className="p-1">
      <div className="flex flex-col gap-6">
        {/* ── 1. Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight text-ink">
              Good {greetingWord}, {firstName}.
            </h1>
            {tagline && <p className="mt-2 text-sm text-ink-muted">{tagline}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              title="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
              <ArrowsClockwise size={16} className={hsLoading ? 'animate-spin text-status-under-process' : ''} />
            </button>
            <Link to="/tickets/new">
              <Button variant="primary"><Plus size={15} weight="bold" />New Ticket</Button>
            </Link>
          </div>
        </div>

        {/* ── 2. Priority alert banner ── */}
        {criticalCount > 0 && (
          <div className="flex items-center gap-3 rounded-card border border-status-escalated/30 bg-status-escalated/8 px-4 py-3">
            <WarningCircle size={20} weight="fill" className="flex-none text-status-escalated" />
            <div className="text-sm">
              <span className="font-semibold text-status-escalated">
                {criticalCount} Critical ticket{criticalCount > 1 ? 's' : ''}
              </span>
              <span className="ml-1 text-ink-muted">require immediate attention</span>
            </div>
            <Link
              to="/tickets?priority=critical"
              className="ml-auto text-sm font-medium text-status-escalated hover:underline">
              View all →
            </Link>
          </div>
        )}

        {/* ── 3. 6 KPI cards ── */}
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 xl:grid-cols-6">
          {STATUS_ORDER.map((status) => {
            const count = getStatusCount(stats, status);
            const Icon = STATUS_ICONS[status];
            const isEscalated = status === 'escalated';
            return (
              <Link key={status} to={`/tickets?status=${status}`}
                className="group block rounded-card border border-paper-border bg-paper-panel p-4.5 shadow-soft transition hover:border-paper-border2">
                <div className="mb-3 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT[status] || 'bg-ink-faint'}`} />
                  <Icon size={12} className="text-ink-faint" weight="regular" />
                  <span className="text-xs text-ink-muted">{STATUS_LABELS[status]}</span>
                </div>
                <div className={`font-serif text-3xl ${isEscalated && count > 0 ? 'text-status-escalated' : 'text-ink'}`}>
                  {count}
                </div>
              </Link>
            );
          })}
        </div>

        {/* ── 4. Two-column: Priority Breakdown + Overview ── */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Priority Breakdown */}
          <Card className="p-5">
            <div className="mb-4 font-serif text-base text-ink">Priority Breakdown</div>
            <div className="space-y-3.5">
              {PRIORITY_ROWS.map(({ key, label }) => {
                const cfg = PRIORITY[key] || {};
                const count = getPriorityCount(stats, key);
                const pct = Math.round((count / priorityTotal) * 100);
                return (
                  <Link key={key} to={`/tickets?priority=${key}`} className="block hover:opacity-80">
                    <div className="mb-1 flex justify-between text-sm">
                      <span className={`font-medium ${cfg.text || 'text-ink'}`}>{label}</span>
                      <span className="text-ink-muted">{count}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-paper-muted">
                      <div className={`h-full rounded-full transition-all ${cfg.dot || 'bg-ink-faint'}`}
                           style={{ width: `${pct}%` }} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>

          {/* Overview */}
          <Card className="p-5">
            <div className="mb-4 font-serif text-base text-ink">Overview</div>
            <div className="flex flex-col">
              <OverviewRow label="Total Open Tickets" value={totalOpen} tone="text-ink" />
              <OverviewRow label="Resolved" value={getStatusCount(stats, 'resolved')} tone="text-status-resolved" />
              <OverviewRow label="Pending Finance" value={getStatusCount(stats, 'pending_third_party')} tone="text-status-pending-finance" />
              <OverviewRow label="Closed" value={getStatusCount(stats, 'closed')} tone="text-ink-muted" last />
            </div>
          </Card>
        </div>

        {/* ── 5. Quick Actions ── */}
        <Card className="p-5">
          <div className="mb-4 font-serif text-base text-ink">Quick Actions</div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            <QuickAction to="/tickets/new" Icon={Plus} label="Create New Ticket" tone="text-status-under-process" />
            <QuickAction to="/tickets?status=new" Icon={Tray} label="View New Tickets" tone="text-ink" />
            <QuickAction to="/tickets?priority=critical" Icon={WarningCircle} label="Critical Issues" tone="text-status-escalated" />
            <QuickAction to="/tickets?status=pending_third_party" Icon={Clock} label="Pending Finance" tone="text-status-pending-finance" />
          </div>
        </Card>

        {/* ── 6. Recent Tickets ── */}
        <section className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
          <div className="flex items-center justify-between border-b border-paper-border bg-paper-muted/40 px-5 py-4">
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-base text-ink">Recent Tickets</h3>
              {hsLoading && <ArrowsClockwise size={14} className="animate-spin text-status-under-process" />}
            </div>
            <Link to="/tickets" className="text-sm font-medium text-status-under-process hover:underline">
              View all →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-paper-border bg-paper-muted/40">
                  <Th>Ticket</Th>
                  <Th>Customer</Th>
                  <Th>Entity</Th>
                  <Th>Priority</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-sm text-ink-faint">
                      No tickets yet
                    </td>
                  </tr>
                )}
                {recent.map((row) => (
                  <tr
                    key={row._key}
                    onClick={() => row.portalId
                      ? onOpenPortalTicket(row.portalId)
                      : onOpenHsPreview(row)}
                    className="cursor-pointer border-b border-paper-border hover:bg-paper-muted/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="text-sm font-semibold text-status-under-process">{row.ref || '—'}</div>
                        {row.source === 'hubspot' && <HsInline />}
                      </div>
                      <div className="mt-0.5 max-w-xs truncate text-xs text-ink-faint" dir="auto">{row.subject}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink" dir="auto">{row.customerName || '—'}</td>
                    <td className="px-4 py-3 text-xs text-ink-muted">{row.entityName || '—'}</td>
                    <td className="px-4 py-3">
                      {row.priority ? <PriorityChip priority={row.priority} /> : <span className="text-xs text-ink-faint">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {row.status
                        ? <StatusBadge status={row.status} />
                        : row.source === 'hubspot'
                          ? <HubspotBadge />
                          : <span className="text-xs text-ink-faint">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-faint">
                      {row.displayDate
                        ? formatDistanceToNow(new Date(row.displayDate), { addSuffix: true })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

// ── local helpers ──────────────────────────────────────────────────────────

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint first:px-5">
      {children}
    </th>
  );
}

function OverviewRow({ label, value, tone, last }) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${last ? '' : 'border-b border-paper-border'}`}>
      <span className="text-sm text-ink-muted">{label}</span>
      <span className={`font-serif text-lg ${tone}`}>{value}</span>
    </div>
  );
}

function QuickAction({ to, Icon, label, tone }) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-field px-3 py-2.5 transition hover:bg-paper-muted">
      <Icon size={18} className={tone} />
      <span className="text-sm text-ink">{label}</span>
    </Link>
  );
}
